/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/createAccount.scss';
import {CreateAccountStep, ReferralType, ResetErrorHandler} from '../../../util/interfaces';
import {
    AwesomeIcon,
    capitalize,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    ElementPosition,
    Loader,
    RenderFile,
    voidFunction
} from '@dvrd/dvr-controls';
import React, {KeyboardEventHandler, MouseEventHandler, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import User from '../../../models/userModel';
import {Icon} from '@iconify/react';
import wavingHand from '@iconify/icons-noto/waving-hand';
import Account from '../../../models/accountModel';
import {formatInvoiceNumber, replaceDateVariables} from '../../../util/utils';
import userCircle from '@iconify/icons-fa-solid/user-circle';
import signOut from '@iconify/icons-fa/sign-out';
import classNames from 'classnames';
import {CompanyErrors, PersonalErrors, ReferralErrors, SettingErrors} from '../../settings/common/userErrorsInterface';
import CountryField from '../../controls/country/field/countryField';
import {BolmateButton, BolmateInput, BolmateNumberInput} from '../../controls';
import PlatformLabelInput from '../../controls/input/platformLabelInput';
import Image from '../../misc/image';

interface Props {
    onChangeUser: ChangeKeyFunction;
    onChangeAccount: ChangeKeyFunction;
    onChangeSetting: ChangeKeyFunction;
    onStepBack: MouseEventHandler;
    onStepNext: (evt: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent | boolean) => void;
    onSignOut: MouseEventHandler;
    onResetUserError: ResetErrorHandler<PersonalErrors>;
    onResetAccountError: ResetErrorHandler<CompanyErrors>;
    onResetSettingError: ResetErrorHandler<SettingErrors>;
    onResetReferralError: ResetErrorHandler<ReferralErrors>;
    onChangeLogo: (file: File) => void;
    onChangeInvoiceNum: (nextNum: string) => void;
    onChangeReferralType: (type: ReferralType) => ChangeFunction<boolean>;
    onChangeReferralOther: ChangeFunction<string>;
    onSubmit: MouseEventHandler & KeyboardEventHandler;
    onCancel: MouseEventHandler;
    step: CreateAccountStep;
    user: User;
    account: Account;
    userErrors: PersonalErrors;
    accountErrors: CompanyErrors;
    settingErrors: SettingErrors;
    logo: RenderFile | null;
    invoicePreview: string;
    loading: boolean;
    isFirstAccount: boolean | null;
    referralType: ReferralType | null;
    referralOther: string;
    referralErrors: ReferralErrors;
}

const stepCount: Record<string, number> = {
    [CreateAccountStep.PERSONAL]: 1,
    [CreateAccountStep.COMPANY]: 2,
    [CreateAccountStep.ADDITIONAL]: 3,
    [CreateAccountStep.INVOICE]: 4,
    [CreateAccountStep.REFERRAL]: 5
};

export default function CreateAccount(props: Props) {
    const {
        account,
        onChangeAccount,
        accountErrors,
        onResetAccountError,
        isFirstAccount,
        onResetUserError,
        user,
        onChangeUser,
        onStepNext,
        userErrors,
        onResetSettingError,
        step,
        settingErrors,
        onChangeSetting,
        onSubmit,
        loading,
        onChangeInvoiceNum,
        invoicePreview,
        onChangeLogo,
        logo,
        onStepBack,
        onCancel,
        onSignOut,
        onChangeReferralType,
        onChangeReferralOther,
        referralType,
        referralOther,
        referralErrors,
        onResetReferralError
    } = props;
    const {t} = useTranslation();
    const [logoDragging, setLogoDragging] = useState(false);
    const logoInput = useRef<HTMLInputElement>(null);
    const dragCounter = useRef<number>(0);

    function onClickUpload() {
        logoInput.current?.click();
    }

    function onDrop(evt: React.DragEvent) {
        evt.preventDefault();
        evt.stopPropagation();
        const {files} = evt.dataTransfer;
        if (files?.length) onChangeLogo(files[0]);
        evt.dataTransfer.clearData();
        setLogoDragging(false);
        dragCounter.current = 0;
    }

    function onPreventDragEvent(evt: React.DragEvent) {
        evt.preventDefault();
        evt.stopPropagation();
    }

    function onDragEnter(evt: React.DragEvent) {
        evt.preventDefault();
        evt.stopPropagation();
        dragCounter.current += 1;
        setLogoDragging(true);
    }

    function onDragLeave(evt: React.DragEvent) {
        evt.preventDefault();
        evt.stopPropagation();
        if (logoDragging) {
            dragCounter.current -= 1;
            if (dragCounter.current <= 0) setLogoDragging(false);
        }
    }

    function onChangeFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (files?.length) onChangeLogo(files[0]);
    }

    function renderStep() {
        switch (step) {
            case CreateAccountStep.WELCOME:
                return renderWelcome();
            case CreateAccountStep.PERSONAL:
                return renderPersonal();
            case CreateAccountStep.COMPANY:
                return renderCompany();
            case CreateAccountStep.ADDITIONAL:
                return renderAdditional();
            case CreateAccountStep.INVOICE:
                return renderInvoice();
            case CreateAccountStep.REFERRAL:
                return renderReferral();
            case CreateAccountStep.FINISHED:
                return renderFinished();
        }
    }

    function renderNextButton(onClick?: MouseEventHandler) {
        return <BolmateButton onClick={onClick ?? onStepNext} label={t('account.create.step.next', 'Volgende')} />;
    }

    function renderBackButton() {
        return (
            <BolmateButton
                onClick={onStepBack}
                label={t('account.create.step.back', 'Terug')}
                secondary
                ornaments={{element: <AwesomeIcon name='arrow-left' />}}
            />
        );
    }

    function renderSkip(onClick?: MouseEventHandler) {
        const click = () => {
            onStepNext(true);
        };
        return (
            <label className='skip-label' onClick={onClick ?? click}>
                {t('account.create.step.skip', 'Overslaan')}
            </label>
        );
    }

    function renderWelcome() {
        return (
            <div className='welcome-container'>
                <label className='step-title'>
                    <Icon icon={wavingHand} className='wave-icon' />{' '}
                    {t('account.create.welcome.title', 'Hi {{name}}, welkom bij Bolmate!', {
                        name: capitalize(user.firstname)
                    })}
                </label>
                <label className='text'>
                    {t('account.create.welcome.text1', 'Je account is succesvol aangemaakt.')}
                </label>
                <label className='text'>
                    {t('account.create.welcome.text2', 'Laten we het aanpassen naar jouw wens.')}
                </label>
                <BolmateButton
                    onClick={onStepNext}
                    label={t('account.create.welcome.start', 'Nu beginnen')}
                    className='start-btn'
                    ornaments={{
                        element: <AwesomeIcon name='arrow-right' />,
                        placement: ElementPosition.RIGHT
                    }}
                />
            </div>
        );
    }

    function renderPersonal() {
        return (
            <div className='step-content'>
                <label className='step-title'>{t('account.create.personal.title', 'Persoonlijke gegevens')}</label>
                <label className='step-sub'>
                    {t('account.create.personal.subtitle', 'Zo houden we contact met elkaar')}
                </label>
                <div className='fields'>
                    <BolmateInput
                        onChange={onChangeUser('firstname')}
                        value={user.firstname}
                        label={t('account.create.personal.firstname', 'Voornaam *')}
                        error={userErrors.firstname}
                        onFocus={onResetUserError('firstname')}
                        onEnter={onStepNext}
                        inputProps={{placeholder: t('account.create.personal.firstnamePlaceholder', 'Je voornaam')}}
                    />
                    <BolmateInput
                        value={user.lastname}
                        onChange={onChangeUser('lastname')}
                        label={t('account.create.personal.lastname', 'Achternaam *')}
                        error={userErrors.lastname}
                        onFocus={onResetUserError('lastname')}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={user.email}
                        onChange={onChangeUser('email')}
                        label={t('account.create.personal.email', 'E-mailadres * (om mee in te loggen)')}
                        error={userErrors.email}
                        onFocus={onResetUserError('email')}
                        inputProps={{type: 'email'}}
                        disabled={user.isGoogleAccount}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={user.telNumber}
                        onChange={onChangeUser('tel_number')}
                        label={t('account.create.personal.phone', 'Telefoonnummer *')}
                        error={userErrors.phone}
                        onFocus={onResetUserError('phone')}
                        inputProps={{type: 'tel'}}
                        onEnter={onStepNext}
                        autoFocus
                    />
                    <BolmateInput
                        value={user.address}
                        onChange={onChangeUser('address')}
                        label={t('account.create.personal.address', 'Adres *')}
                        error={userErrors.address}
                        onFocus={onResetUserError('address')}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={user.zipCode}
                        onChange={onChangeUser('zip_code')}
                        label={t('account.create.personal.zipCode', 'Postcode *')}
                        error={userErrors.zipCode}
                        onFocus={onResetUserError('zipCode')}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={user.city}
                        onChange={onChangeUser('city')}
                        label={t('account.create.personal.city', 'Stad *')}
                        error={userErrors.city}
                        onFocus={onResetUserError('city')}
                        onEnter={onStepNext}
                    />
                    <CountryField
                        onChange={onChangeUser('country')}
                        value={user.country}
                        itemsPosition='top'
                        labelAsValue
                    />
                </div>
                <div className='btns-container single'>{renderNextButton()}</div>
            </div>
        );
    }

    function renderCompany() {
        return (
            <div className='step-content'>
                <label className='step-title'>{t('account.create.company.title', 'Bedrijfsgegevens')}</label>
                <label className='step-sub'>
                    {t(
                        'account.create.company.subtitle',
                        'Deze gegevens vullen we ook alvast in op je factuurtemplate'
                    )}
                </label>
                <div className='fields'>
                    <BolmateInput
                        value={account.companyName}
                        onChange={onChangeAccount('company_name')}
                        label={t('account.create.company.companyName', 'Bedrijfsnaam *')}
                        error={accountErrors.companyName}
                        onFocus={onResetAccountError('companyName')}
                        onEnter={onStepNext}
                        autoFocus
                    />
                    <PlatformLabelInput
                        onChange={onChangeAccount('platform_label')}
                        value={account.platformLabel}
                        placeholder={account.companyName}
                    />
                    <BolmateInput
                        value={account.email}
                        onChange={onChangeAccount('email')}
                        label={t('account.create.company.email', 'E-mailadres * (ontvangst van klanten mails)')}
                        error={accountErrors.email}
                        onFocus={onResetAccountError('email')}
                        onEnter={onStepNext}
                        inputProps={{type: 'email'}}
                    />
                    <BolmateInput
                        value={account.telNumber}
                        onChange={onChangeAccount('tel_number')}
                        label={t('account.create.company.phone', 'Telefoonnummer *')}
                        error={accountErrors.phone}
                        onFocus={onResetAccountError('phone')}
                        inputProps={{type: 'tel'}}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={account.address}
                        onChange={onChangeAccount('address')}
                        label={t('account.create.company.address', 'Adres *')}
                        error={accountErrors.address}
                        onFocus={onResetAccountError('address')}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={account.zipCode}
                        onChange={onChangeAccount('zip_code')}
                        label={t('account.create.company.zipCode', 'Postcode *')}
                        error={accountErrors.zipCode}
                        onFocus={onResetAccountError('zipCode')}
                        onEnter={onStepNext}
                    />
                    <BolmateInput
                        value={account.city}
                        onChange={onChangeAccount('city')}
                        label={t('account.create.company.city', 'Stad *')}
                        error={accountErrors.city}
                        onFocus={onResetAccountError('city')}
                        onEnter={onStepNext}
                    />
                    <CountryField
                        onChange={onChangeAccount('country')}
                        value={account.country}
                        itemsPosition='top'
                        labelAsValue
                    />
                </div>
                <div className='btns-container'>
                    {renderBackButton()}
                    {renderNextButton()}
                </div>
            </div>
        );
    }

    function renderAdditional() {
        return (
            <div className='step-content'>
                <label className='step-title'>
                    {t('account.create.additional.title', 'Aanvullende bedrijfsgegevens')}
                </label>
                <label className='step-sub'>
                    {t(
                        'account.create.additional.subtitle',
                        'Deze gegevens vullen we ook vast in op je factuurtemplate'
                    )}
                </label>
                <div className='fields'>
                    <BolmateInput
                        value={account.cocNumber}
                        onChange={onChangeAccount('coc_number')}
                        label={t('account.create.additional.cocNumber', 'KvK nummer')}
                        onEnter={onStepNext}
                        autoFocus
                    />
                    <BolmateInput
                        value={account.vatNumber}
                        onChange={onChangeAccount('vat_number')}
                        label={t('account.create.additional.vatNumber', 'BTW nummer')}
                        onEnter={onStepNext}
                    />
                </div>
                <div className='btns-container skip'>
                    {renderBackButton()}
                    {renderNextButton()}
                    {renderSkip()}
                </div>
            </div>
        );
    }

    function renderInvoice() {
        const {settings} = account;
        return (
            <div className='step-content'>
                <label className='step-title'>
                    {t('account.create.invoice.title', 'Bedrijfslogo en factuurnummer')}
                </label>
                <label className='step-sub'>
                    {t('account.create.invoice.subtitle', 'Deze zetten we op de facturen')}
                </label>
                {renderLogo()}
                <div className='invoice-number'>
                    <div className='row'>
                        <BolmateInput
                            value={settings.invoicePrefix}
                            onChange={onChangeSetting('invoice_prefix')}
                            label={t('account.create.invoice.prefix', 'Voortekens *')}
                            error={settingErrors.invoicePrefix}
                            onFocus={onResetSettingError('invoicePrefix')}
                            onEnter={onStepNext}
                            autoFocus
                        />
                        <BolmateNumberInput
                            value={settings.invoiceStart}
                            onChange={onChangeSetting('invoice_start')}
                            label={t('account.create.invoice.start', 'Beginnen bij *')}
                            error={settingErrors.invoiceStart}
                            onFocus={onResetSettingError('invoiceStart')}
                            onEnter={onStepNext}
                            wholeNumbers
                        />
                    </div>
                    {renderInvoicePreview()}
                </div>
                <div className='btns-container skip'>
                    {renderBackButton()}
                    {renderNextButton()}
                    {renderSkip()}
                </div>
            </div>
        );
    }

    function renderLogo() {
        return (
            <div
                className={classNames('upload-container', logoDragging && 'dragging')}
                onClick={onClickUpload}
                onDrag={onPreventDragEvent}
                onDragOver={onPreventDragEvent}
                onDrop={onDrop}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
            >
                <input
                    type='file'
                    accept='image/png, image/jpeg'
                    onChange={onChangeFile}
                    style={{display: 'none'}}
                    ref={logoInput}
                />
                {logo === null ? (
                    <div className='logo-container empty'>
                        <AwesomeIcon name='image' className='file-icon' />
                        <label className='drop-message'>
                            {t('account.create.invoice.dropMessage', 'Sleep hier je logo of klik hier om te zoeken')}
                        </label>
                        <label className='support'>
                            {t('account.create.invoice.supported', 'Ondersteunde bestanden: PNG, JPG en JPEG')}
                        </label>
                    </div>
                ) : (
                    <div className='logo-container'>
                        <img src={logo.src} alt='' className='logo' />
                        <label className='filename'>{logo.filename}</label>
                        <label className='support'>
                            {t('account.create.invoice.supported', 'Ondersteunde bestanden: PNG, JPG en JPEG')}
                        </label>
                    </div>
                )}
            </div>
        );
    }

    function renderInvoicePreview() {
        const {settings} = account;
        const {invoicePrefix, invoiceStart} = settings;
        const count = Number(invoicePreview);
        const editable = !Number.isNaN(count);

        function onClick(modifier: 1 | -1) {
            return function () {
                const nextCount = count + modifier;
                const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
                onChangeInvoiceNum(nextNum);
            };
        }

        const prefix = replaceDateVariables(invoicePrefix);
        const min = Number(settings.invoiceStart);
        return (
            <div className='invoice-number-preview'>
                <label className='preview'>
                    {t('account.create.invoice.preview', 'Voorbeeld:')} {prefix}
                    {invoicePreview}
                </label>
                {editable && (
                    <div className='editors'>
                        <span className='editor' onClick={onClick(1)}>
                            +
                        </span>
                        <span
                            className={classNames('editor', count <= min && 'disabled')}
                            onClick={count > min ? onClick(-1) : voidFunction}
                        >
                            -
                        </span>
                    </div>
                )}
            </div>
        );
    }

    function renderReferral() {
        return (
            <div className='step-content'>
                <label className='step-title'>{t('account.create.referral.title', 'Hoe heb je ons gevonden?')}</label>
                <label className='step-sub'>
                    {t('account.create.referral.subtitle', 'We zijn erg benieuwd naar hoe je ons gevonden hebt.')}
                </label>
                <div className='referral-checks'>
                    {!!referralErrors.referralType && (
                        <label className='referral-error'>
                            {t('account.create.referral.chooseOption', 'Kies een optie')}
                        </label>
                    )}
                    {renderReferralCheck(ReferralType.GOOGLE, t('account.create.referral.google', 'Via Google'))}
                    {referralType === ReferralType.GOOGLE && (
                        <BolmateInput
                            onChange={onChangeReferralOther}
                            value={referralOther}
                            placeholder={t('account.create.referral.googlePlaceholder', 'Gezocht op zoekwoord...')}
                            error={referralErrors.referralOther}
                            onFocus={onResetReferralError('referralOther')}
                        />
                    )}
                    {renderReferralCheck(
                        ReferralType.SOCIAL,
                        t('account.create.referral.social', 'Via Socials (Facebook groepen, X, Instagram, etc.)')
                    )}
                    {renderReferralCheck(
                        ReferralType.EXISTING_CUSTOMER,
                        t('account.create.referral.existingCustomer', 'Via Via')
                    )}
                    {renderReferralCheck(ReferralType.BOL, t('account.create.referral.bol', 'Via Bol'))}
                    {renderReferralCheck(ReferralType.OTHER, t('account.create.referral.other', 'Anders, namelijk...'))}
                    {referralType === ReferralType.OTHER && (
                        <BolmateInput
                            onChange={onChangeReferralOther}
                            value={referralOther}
                            placeholder={t('account.create.referral.otherPlaceholder', 'Gevonden via...')}
                            error={referralErrors.referralOther}
                            onFocus={onResetReferralError('referralOther')}
                        />
                    )}
                </div>
                <div className='btns-container skip'>
                    {renderBackButton()}
                    {renderNextButton(onSubmit)}
                    {renderSkip()}
                </div>
            </div>
        );
    }

    function renderReferralCheck(type: ReferralType, label: string) {
        const checked = referralType === type;
        return (
            <Checkbox
                checked={checked}
                onCheck={onChangeReferralType(type)}
                label={label}
                className={classNames(!!referralErrors.referralType && 'type-error')}
                asRadio
            />
        );
    }

    function renderFinished() {
        return (
            <div className='step-content'>
                <label className='step-title'>{t('account.create.finished.title', 'Account ingesteld')}</label>
                <label className='step-sub'>
                    {t('account.create.finished.subtitle', 'Je account is ingesteld en is klaar voor gebruik.')}
                </label>
                <AwesomeIcon name='check-circle' className='check-icon' />
                <div className='btns-container single'>{renderNextButton()}</div>
            </div>
        );
    }

    function renderHeader() {
        return (
            <div className='header-container'>
                {!isFirstAccount && (
                    <BolmateButton
                        onClick={onCancel}
                        className='cancel'
                        label={t('account.create.cancel', 'Annuleren')}
                    />
                )}
                <Image name='logo' className='logo' />
                <div className='user-container'>
                    <Icon icon={userCircle} className='user-icon' />
                    <label className='user-name'>{user.fullName}</label>
                    {isFirstAccount && (
                        <div className='sign-out-btn' onClick={onSignOut}>
                            <Icon icon={signOut} className='sign-out-logo' />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderStepCounter() {
        if (!Object.hasOwn(stepCount, step)) return null;
        const count = stepCount[step];
        const maxSteps = user.isNewUser ? 5 : 4;
        return (
            <div className='step-counter'>
                <label className='step-count'>
                    {count}/{maxSteps}
                </label>
                <div className='steps'>
                    <div className={classNames('step', count >= 1 && 'done')} />
                    <div className={classNames('step', count >= 2 && 'done')} />
                    <div className={classNames('step', count >= 3 && 'done')} />
                    <div className={classNames('step', count >= 4 && 'done')} />
                    {user.isNewUser && <div className={classNames('step', count >= 5 && 'done')} />}
                </div>
            </div>
        );
    }

    function renderStepTitle() {
        return (
            <>
                <label className='content-title'>
                    {t('account.create.contentTitle', 'Laten we je account klaarmaken voor gebruik')}
                </label>
                <label className='content-sub'>
                    {t(
                        'account.create.contentSub',
                        'We zullen deze informatie gebruiken om je beter van dienst te kunnen zijn'
                    )}
                </label>
                <div className='referral-fields'></div>
            </>
        );
    }

    return (
        <div className='create-account'>
            {renderHeader()}
            <div className='content'>
                <Loader active={loading} />
                {![CreateAccountStep.WELCOME, CreateAccountStep.FINISHED].includes(step) && (
                    <div className='content-title-container'>
                        {renderStepTitle()}
                        {renderStepCounter()}
                    </div>
                )}
                {renderStep()}
            </div>
        </div>
    );
}
